import {
  BellIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";

// Pure display logic for the Action suggestion's step timeline: grouping repeated
// steps, deriving timing/cadence labels, and the per-type icon/description fallbacks.
// No React here — DraftSuggestionCard and its step components consume this.

// Per-type row icon + tile colour. The catalogue of step types grows server-side,
// so unknown types fall back to a generic dot rather than throwing.
const STEP_ICONS = {
  wait_until: { Icon: ClockIcon, className: "bg-zinc-100 text-zinc-500" },
  wait_duration: { Icon: ClockIcon, className: "bg-zinc-100 text-zinc-500" },
  "payment.request.send": {
    Icon: PaperAirplaneIcon,
    className: "bg-orange-100 text-orange-500",
  },
  notify: { Icon: BellIcon, className: "bg-blue-100 text-blue-500" },
  escalate: {
    Icon: ExclamationTriangleIcon,
    className: "bg-amber-100 text-amber-600",
  },
  complete: { Icon: CheckCircleIcon, className: "bg-green-100 text-green-600" },
  cancel: { Icon: XCircleIcon, className: "bg-zinc-100 text-zinc-500" },
  human_approval: {
    Icon: ClipboardDocumentCheckIcon,
    className: "bg-blue-100 text-blue-500",
  },
  "calendar.event.create": {
    Icon: CalendarDaysIcon,
    className: "bg-blue-100 text-blue-500",
  },
};
const DEFAULT_STEP_ICON = {
  Icon: ClockIcon,
  className: "bg-zinc-100 text-zinc-400",
};

export function stepIcon(stepType) {
  return STEP_ICONS[stepType] ?? DEFAULT_STEP_ICON;
}

// "payment.request.send" -> "Payment request send" when the model omits a description.
function prettifyStepType(stepType) {
  const words = (stepType ?? "").split(/[._-]/).filter(Boolean);
  if (!words.length) return "Step";
  return words
    .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

const STEP_TYPE_FALLBACK_DESCRIPTIONS = {
  wait_until: "Wait",
  wait_duration: "Wait",
  "payment.request.send": "Send payment request",
  notify: "Send notification",
  escalate: "Escalate",
  complete: "Complete",
  cancel: "Cancel",
  human_approval: "Request approval",
};

// Wait steps only exist to delay what follows; their fire time is now carried onto
// the next step's timing chip, so they're dropped from the visible timeline.
const WAIT_STEP_TYPES = new Set(["wait_until", "wait_duration"]);
const SEND_STEP_TYPE = "payment.request.send";

function stepDescription(step) {
  return (
    step.description ??
    STEP_TYPE_FALLBACK_DESCRIPTIONS[step.stepType] ??
    prettifyStepType(step.stepType)
  );
}

// Summary line for a collapsed run of same-type steps whose descriptions differ,
// e.g. five per-instalment calendar entries -> "Create 5 calendar events".
const COLLAPSED_STEP_LABELS = {
  "calendar.event.create": (n) => `Create ${n} calendar events`,
  "payment.request.send": (n) => `Send ${n} payment requests`,
  notify: (n) => `Send ${n} notifications`,
  wait_until: (n) => `${n} scheduled waits`,
  wait_duration: (n) => `${n} scheduled waits`,
};

function collapsedDescription(stepType, count) {
  const label = COLLAPSED_STEP_LABELS[stepType];
  if (label) return label(count);
  const base =
    STEP_TYPE_FALLBACK_DESCRIPTIONS[stepType] ?? prettifyStepType(stepType);
  return `${count} × ${base}`;
}

// The line shown for a group: the shared description when every step reads the same
// (preserving amounts/dates), otherwise a count summary of the collapsed run.
function groupDescription(stepType, steps) {
  // A run of same-type steps with no authored description (e.g. steps read from the action
  // API, which carries no per-step text) reads best as a count summary — "Create 5 calendar
  // events" — rather than repeating one generic type label. Authored descriptions (the
  // suggestion card, or a synthesised payment amount) keep the logic below.
  if (
    steps.length > 1 &&
    COLLAPSED_STEP_LABELS[stepType] &&
    steps.every((s) => !s.description)
  ) {
    return collapsedDescription(stepType, steps.length);
  }

  const descriptions = steps.map(stepDescription);
  if (descriptions.every((d) => d === descriptions[0])) return descriptions[0];
  return collapsedDescription(stepType, steps.length);
}

// A step's grouping key: same type AND same amount share a row. Payment sends are excluded from
// merging by groupSteps (each instalment renders as its own row); the key still groups other
// repeated step types such as a plan's calendar entries.
function stepGroupKey(step) {
  const amount = step.payload?.amount_requested;
  return `${step.stepType}|${amount != null ? String(amount) : ""}`;
}

// Group the visible steps into timeline rows. Payment sends are NEVER merged — each instalment
// shows as its own row — but other repeated same-type steps (e.g. a plan's calendar entries) still
// collapse into one count summary. Wait steps never render; each carries its fire time onto the
// next visible step's effectiveExecuteAfter so that step's timing chip is right.
export function groupSteps(steps) {
  const groups = [];
  let lastFireAt = null;
  for (const step of steps ?? []) {
    if (step.executeAfter) lastFireAt = step.executeAfter;
    if (WAIT_STEP_TYPES.has(step.stepType)) continue;
    const annotated = { ...step, effectiveExecuteAfter: step.executeAfter ?? lastFireAt };
    const key = stepGroupKey(step);
    const last = groups[groups.length - 1];
    if (last && last.key === key && step.stepType !== SEND_STEP_TYPE) {
      last.steps.push(annotated);
    } else {
      groups.push({ key, stepType: step.stepType, steps: [annotated] });
    }
  }
  return groups.map((g) => ({
    ...g,
    description: groupDescription(g.stepType, g.steps),
  }));
}

// Payment plan / promise to pay cards show only the scheduled payment sends. The plan the
// model composes also carries plumbing steps around them — calendar entries, a
// missed-payment follow-up task, a paid-status check — which are internal and shouldn't
// appear as timeline rows. Wait steps are kept so groupSteps can carry their fire time onto
// the send that follows (it drops the waits itself).
const SCHEDULED_ACTION_TYPES = new Set(["PaymentPlan", "PromiseToPay"]);
const SCHEDULED_VISIBLE_STEP_TYPES = new Set([
  "wait_until",
  "wait_duration",
  "payment.request.send",
]);

export function visibleActionSteps(steps, type) {
  if (!SCHEDULED_ACTION_TYPES.has(type)) return steps ?? [];
  return (steps ?? []).filter((s) => SCHEDULED_VISIBLE_STEP_TYPES.has(s.stepType));
}

// A display-only leading row for scheduled work (payment plan, promise to pay): the backend
// composes no calendar step (the calendar-service projects the sends from the action's
// lifecycle events), and visibleActionSteps hides the internal calendar plumbing, so the card
// prepends this one clean "Schedule … in calendar" row. Skipped if the visible groups already
// carry a real calendar step.
const CALENDAR_LEAD_COPY = {
  PaymentPlan: "Schedule payment plan in calendar",
  PromiseToPay: "Schedule promise to pay in calendar",
};

// A merchant-scheduled payment request shares PromiseToPay's type (see useSignalChat's alias), so
// its own lead copy is picked by `kind` rather than a type entry above.
const SCHEDULE_CALENDAR_LEAD_COPY = "Schedule payment request in calendar";

export function calendarLeadRow(type, groups, kind) {
  const description =
    type === "PromiseToPay" && kind === "schedule"
      ? SCHEDULE_CALENDAR_LEAD_COPY
      : CALENDAR_LEAD_COPY[type];
  if (!description) return null;
  if (groups.some((g) => g.stepType === "calendar.event.create")) return null;
  return {
    key: "calendar-lead",
    stepType: "calendar.event.create",
    steps: [{ effectiveExecuteAfter: null }],
    description,
  };
}

// Best-effort cadence info from the executeAfter spacing of a repeated group. Returns
// null when the spacing isn't regular enough to describe simply.
export function cadenceInfo(group) {
  const dates = group.steps
    .map((s) => s.effectiveExecuteAfter)
    .filter(Boolean)
    .map((d) => new Date(d));
  if (dates.length < 2) return null;

  const dayMs = 24 * 60 * 60 * 1000;
  const diffs = dates.slice(1).map((d, i) => Math.round((d - dates[i]) / dayMs));
  const [intervalDays, ...rest] = diffs;
  if (!intervalDays || rest.some((d) => d !== intervalDays)) return null;

  const count = group.steps.length;
  if (intervalDays % 7 === 0) {
    const weeks = intervalDays / 7;
    return { kind: "weekly", weeks, weekday: format(dates[0], "EEEE"), count };
  }
  return { kind: "daily", intervalDays, count };
}

// "Every Friday" / "Every 3 days" — the count rides the timing chip (cadenceSpanLabel).
export function cadenceLabel(group) {
  const info = cadenceInfo(group);
  if (!info) return null;
  if (info.kind === "weekly") {
    return info.weeks === 1
      ? `Every ${info.weekday}`
      : `Every ${info.weeks} weeks on ${info.weekday}`;
  }
  return `Every ${info.intervalDays} days`;
}

// The count shown on the timing chip beside the date range: "7 weeks" for a weekly run,
// "5 days" for a daily one. Null when the spacing isn't a regular cadence.
function cadenceSpanLabel(group) {
  const info = cadenceInfo(group);
  if (!info) return null;
  const unit = info.kind === "weekly" ? "week" : "day";
  return `${info.count} ${unit}${info.count === 1 ? "" : "s"}`;
}

// The design's timing chip: with no effective fire time it reads as "Now"; otherwise
// the resolved fire date and time (inherited from a preceding wait when the step has none
// of its own), or a "start – end · N weeks" range across a collapsed run of repeated steps.
// The timeline only renders for multi-step suggestions, so a concrete row always carries the
// execution time alongside its date.
export function timingLabel(group) {
  const dates = group.steps
    .map((s) => s.effectiveExecuteAfter)
    .filter(Boolean)
    .map((d) => new Date(d));
  if (!dates.length) return "Now";
  if (dates.length === 1) return `${format(dates[0], "dd MMM · HH:mm")}`;
  const range = `${format(dates[0], "dd MMM")} – ${format(dates[dates.length - 1], "dd MMM")}`;
  const span = cadenceSpanLabel(group);
  return span ? `${range} · ${span}` : range;
}

// The scheduled send time (24h "HH:mm", e.g. "07:00") of a single-send action, read off the send
// step's resolved fire time — the schedule's own time (system-set), which the model never reliably
// knows. Null for an instant send, a collapsed multi-instalment run, or a non-send group.
//
// Only the ACTIVITY FEED needs this: it has action steps and nothing else. The suggestion card reads
// the send time off the service's resolved schedule (see suggestedPaymentCopy) instead.
export function scheduledSendTime(groups) {
  if (!groups || groups.length !== 1) return null;
  const [group] = groups;
  if (group.stepType !== SEND_STEP_TYPE || group.steps?.length !== 1) return null;
  const iso = group.steps[0]?.effectiveExecuteAfter;
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, "HH:mm");
}

// A trailing " at <time>" the model sometimes bakes into its own prose ("… at 08:23", "… at 7am").
// Stripped so the time does not appear twice when the accurate one is appended.
const TRAILING_TIME = /\s+at\s+\d{1,2}(:\d{2})?\s*(?:am|pm)?$/i;

// Append the schedule's own send time to a promise/request label, normalising to exactly one time.
// Returns the text unchanged when there is no resolvable send time (instant send, or the action
// detail not yet loaded).
export function withScheduledSendTime(text, groups) {
  const time = scheduledSendTime(groups);
  if (!text || !time) return text;
  return `${text.replace(TRAILING_TIME, "")} at ${time}`;
}

// Fallback header for an action the service resolved no payment facts for: the model's own
// explanation, or a count of the repeated sends when it wrote none.
export function actionSummary(steps, explanation) {
  if (explanation && explanation.trim()) return explanation;

  const sends = groupSteps(steps)
    .filter((g) => /send|notify/i.test(g.stepType ?? ""))
    .map((g) => g.steps[0]);
  if (sends.length < 2) return explanation;

  const startsToday =
    !sends[0]?.effectiveExecuteAfter ||
    new Date(sends[0].effectiveExecuteAfter) <= new Date();

  const parts = [`${sends.length}x payments`];
  if (startsToday) parts.push("Starting today");

  return parts.length >= 2 ? parts.join(" · ") : explanation;
}
